'use client';

import { useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useToast } from '@pie/amber';
import { track } from '@upms/analytics';
import { reporter } from '@upms/observability';
import { toConsentErrorCategory } from '@/lib/analytics/consentErrorCategory';
import { CONSENT_EVENTS } from '@/lib/analytics/events';
import { useSubmitConsents } from '@/lib/api/consents';
import { toConsentErrorCode } from '@/lib/api/consents/errors';
import { buildSubmitPayload } from './buildSubmitPayload';
import type { ConsentPrompt } from './deriveConsentPrompt';
import type { ConsentFormValues } from './formValues';
import { findMobilePhoneError, INCOMPLETE_MOBILE_PHONE_ERROR } from './schema';
import { summarizeConsentDecisions } from './summarizeConsentDecisions';

type Options = {
  prompt: Extract<ConsentPrompt, { kind: 'prompt' }>;
  onMobilePhoneError: (message: string) => void;
  onSubmitted: () => void;
  onNothingToSubmit: () => void;
};

const useIdempotencyKey = () => {
  const keyRef = useRef<string | null>(null);
  const payloadRef = useRef<string | null>(null);

  const forPayload = (payloadJson: string): string => {
    if (keyRef.current === null || payloadRef.current !== payloadJson) {
      keyRef.current = uuid();
    }
    payloadRef.current = payloadJson;
    return keyRef.current;
  };

  const reset = () => {
    keyRef.current = null;
  };

  return { forPayload, reset };
};

export const useConsentSubmission = ({
  prompt,
  onMobilePhoneError,
  onSubmitted,
  onNothingToSubmit,
}: Options) => {
  const submitConsents = useSubmitConsents();
  const { toast } = useToast();
  const idempotencyKey = useIdempotencyKey();

  const handleSuccess = (body: ReturnType<typeof buildSubmitPayload>) => {
    track(CONSENT_EVENTS.consentPromptCompleted, summarizeConsentDecisions(body));
    reporter.action('consent_prompt_closed', { feature: 'consent', outcome: 'submitted' });
    onSubmitted();
    toast({
      title: 'Communication preferences',
      body: 'Your selections have been saved.',
      tone: 'positive',
    });
  };

  const handleError = (error: unknown) => {
    reporter.error(
      'Consent submission failed',
      { feature: 'consent', operation: 'submitConsents' },
      error
    );
    track(CONSENT_EVENTS.consentPromptFailed, {
      error_category: toConsentErrorCategory(error),
    });

    const errorCode = toConsentErrorCode(error);

    if (errorCode === 'VALIDATION' || errorCode === 'MISSING_CONTACT_AT_CONSENT') {
      onMobilePhoneError(INCOMPLETE_MOBILE_PHONE_ERROR);
    }

    if (errorCode === 'IDEMPOTENCY_CONFLICT') {
      idempotencyKey.reset();
    }
  };

  const submit = (values: ConsentFormValues) => {
    const mobilePhoneError = findMobilePhoneError(values);
    if (mobilePhoneError) {
      onMobilePhoneError(mobilePhoneError);
      return;
    }

    const body = buildSubmitPayload(prompt, values);

    if (body.decisions.length === 0) {
      onNothingToSubmit();
      return;
    }

    const payloadJson = JSON.stringify(body);

    submitConsents.mutate(
      { body, idempotencyKey: idempotencyKey.forPayload(payloadJson) },
      { onSuccess: () => handleSuccess(body), onError: handleError }
    );
  };

  return { submit, status: submitConsents.status };
};
